'use client';

import { memo, useEffect, useMemo, useState } from 'react';
import { Configure, Hits, Index, useInstantSearch } from 'react-instantsearch';
import { TextButton, Typography } from '@workspace/ui/common';
import {
  AlgoliaPagination,
  LoadingBoundary,
  NoResultsBoundary,
  SearchPageItemHit,
  SearchPageItemHitSkeleton,
  SearchFilterRadio,
  SEARCH_ALL_FILTER_CONFIGS,
  SearchAllFilterOption,
} from '@workspace/widgets';
import { openDocsBot } from '@/app/lib/docsbot';
import { scrollToTop } from '@/app/lib';

interface Props {
  query?: string;
}

const ResultText = () => {
  const { results, status } = useInstantSearch();

  const resultsText = useMemo(() => {
    const isLoading = status === 'loading' || !results || results.__isArtificial;
    if (isLoading) {
      return 'Searching...';
    }

    const total = results.nbHits;
    const query = results.query;
    const safeForText = query.trim() ? `for "${query}"` : '';

    // Handle edge case: no results
    if (total === 0) {
      return `Showing ${total} results ${safeForText}`;
    }

    // Handle edge case: single result
    if (total === 1) {
      return `Showing ${total} result ${safeForText}`;
    }

    // Calculate actual number of hits on current page
    const currentHits = results.hits?.length || 0;
    const from = Math.max(1, Math.min(results.page * results.hitsPerPage + 1, total));
    const to = Math.min(results.page * results.hitsPerPage + currentHits, total);

    // Ensure the range makes sense
    if (from > to) {
      return `Showing ${total} results ${safeForText}`;
    }

    return `Showing ${from} to ${to} from ${total} results ${safeForText}`;
  }, [results, status]);

  return (
    <Typography variant="h4" className="my-sm md:mb-2xl md:mt-0">
      {resultsText}
    </Typography>
  );
};

const PaginationReset = memo(({ query }: { query?: string }) => {
  const { setIndexUiState } = useInstantSearch();
  useEffect(() => {
    // Reset to page 1 when query changes
    setIndexUiState((prevState) => {
      if (prevState.page && prevState.page > 0) {
        return {
          ...prevState,
          page: 0,
        };
      }
      return prevState;
    });
  }, [query, setIndexUiState]);

  return null;
});

export const ClientSearchPage = ({ query }: Props) => {
  const [activeFilter, setActiveFilter] = useState<SearchAllFilterOption>('all');
  const [itemsPerPage, setItemsPerPage] = useState<number>(12);

  const activeIndex = useMemo(() => {
    return SEARCH_ALL_FILTER_CONFIGS.find((config) => config.key === activeFilter)?.refinementValues!;
  }, [activeFilter]);

  return (
    <Index indexName={activeIndex}>
      <div className="px-md pb-4xl md:px-4xl md:pt-2xl porta-container">
        <ResultText />

        <PaginationReset query={query} />
        <Configure query={query} />

        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,11.875rem)_minmax(0,1fr)] md:gap-3xl">
          <SearchFilterRadio
            className="py-xs md:py-0"
            title="Filter"
            activeFilter={activeFilter}
            onChange={setActiveFilter}
          />

          <div>
            <LoadingBoundary>
              <div className="pt-sm md:pt-0 space-y-xs">
                {Array.from({ length: 12 }).map((_, idx) => (
                  <SearchPageItemHitSkeleton key={idx} />
                ))}
              </div>
            </LoadingBoundary>
            <NoResultsBoundary
              fallback={
                <div className="mx-auto flex flex-col flex-1 items-center text-center pt-lg md:pt-0 pb-15 md:pb-0">
                  <Typography variant="h6" className="mb-xs">
                    No results found
                  </Typography>
                  <Typography variant="bodyMd" className="mb-lg">
                    Start a new search or ask Greg, our smart assistant
                  </Typography>
                  <TextButton className="underline" onClick={openDocsBot}>
                    Ask Greg
                  </TextButton>
                </div>
              }
            >
              <Hits
                hitComponent={SearchPageItemHit}
                classNames={{
                  root: 'pt-sm md:pt-0',
                  list: 'space-y-xs',
                  item: '',
                }}
              />
            </NoResultsBoundary>
            <NoResultsBoundary fallback={null}>
              <AlgoliaPagination
                onPageChange={scrollToTop}
                variant="jumper"
                showItemsPerPageSelector={true}
                className="flex-col md:flex-row justify-start mt-lg md:mt-2xl"
                defaultItemsPerPage={itemsPerPage}
                onItemsPerPageChange={setItemsPerPage}
              />
            </NoResultsBoundary>
          </div>
        </div>
      </div>
    </Index>
  );
};
